import {AGENCY_KEYS} from "@/api/agencyApi";
import RefluencedLogoSrc from "@/assets/refluenced_icon.svg";
import Button from "@/components/Common/Button";
import FileUpload from "@/components/Common/FileUpload";
import AddressForm from "@/components/Common/forms/AddressForm";
import Modal from "@/components/Common/Modal";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useAgencyId, useSelectedAgency} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import RegistrationInputField from "@/pages/registerCompany/RegistrationInputField";
import InstagramField from "@/pages/registerCompany/steps/interestSelection/InstagramField";
import InterestSelection from "@/pages/registerCompany/steps/interestSelection/InterestSelection";
import TikTokField from "@/pages/registerCompany/steps/interestSelection/TikTokField";
import {queryClient} from "@/queryClient";
import {createCustomerModalOpenAtom} from "@/store/jotaiAtoms";
import useRegistrationStore from "@/store/registration";
import useUserStore from "@/store/user";
import {handleErrorWithToast} from "@/utils/errorHandlers";
import {useMutation} from "@tanstack/react-query";
import type {AxiosError} from "axios";
import {useSetAtom} from "jotai";
import {isEmpty} from "lodash";
import {useEffect, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router";
import {useEffectOnce} from "react-use";
import {useImmer} from "use-immer";
import {z} from "zod";
import {useShallow} from "zustand/shallow";

const AddressSchema = z.object({
  address_street: z
    .string({message: "company_profile:missing_address_street_error"})
    .min(1, "company_profile:missing_address_street_error")
    .superRefine((val, ctx) => {
      if (val.length && !/\d/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "company_profile:missing_address_street_number_error",
        });
      }
    }),
  address_postal: z
    .string({message: "company_profile:missing_address_postal_error"})
    .min(1, "company_profile:missing_address_postal_error"),
  address_city: z
    .string({message: "company_profile:missing_address_city_error"})
    .min(1, "company_profile:missing_address_city_error"),
});

type TCustomerState = Partial<{
  address_city: string | null;
  address_postal: string | null;
  address_street: string | null;
  bio: string;
  company_name: string;
  country: string | null;
  file: File;
  place_description: string | null;
  place_id: string | null;
  website: string;
}>;

const CreateCustomerModal: FC = () => {
  const {t} = useTranslation(["multi_account", "common"]);
  const {refetch: refetchUser} = useFetchUser();
  const navigate = useNavigate();
  const user = useUserStore(state => state.user);
  const agencyId = useAgencyId();
  const agency = useSelectedAgency();
  const setIsModalOpen = useSetAtom(createCustomerModalOpenAtom);
  const [isDynamicAddressFocused, setDynamicAddressFocused] = useState(false);
  const [state, setState] = useImmer<TCustomerState>({});
  const [errors, setErrors] = useImmer<Record<string, string>>({});
  const [insta, tiktok, interests] = useRegistrationStore(
    useShallow(state => [state.companyInstagramUsername, state.companyTikTokUsername, state.interests]),
  );
  const resetRegistrationStore = useRegistrationStore(state => state.reset);
  const {mutate, isPending} = useMutation({
    mutationKey: AGENCY_KEYS.mAgencySubBrandRegister(agencyId!),
    mutationFn: () => {
      const formData = new FormData();

      for (const key in state) {
        const typedKey = key as keyof typeof state;
        if (!state[typedKey]) continue;
        formData.append(typedKey, state[typedKey]!);
      }

      if (insta) {
        formData.append("username", insta);
      }

      if (tiktok) {
        formData.append("tiktok_username", tiktok);
      }

      if (interests.length > 0) {
        formData.append("interests", interests.join(","));
      }

      if (state.file) {
        formData.append("picture", state.file!);
        formData.append("picture320", state.file!);
        formData.append("picture640", state.file!);
      }

      return client.post(AGENCY_KEYS.mAgencySubBrandRegister(agencyId!)[0], formData, {
        headers: {"Content-Type": "multipart/form-data"},
      });
    },
    onSuccess: async response => {
      await queryClient.invalidateQueries({queryKey: AGENCY_KEYS.qAgencySubbrands(agencyId!)});
      refetchUser();
      setIsModalOpen(false);
      setState({});
      resetRegistrationStore();
      navigate(`/agency/${agencyId}/subbrand/${response.data.id}/edit/`);
    },
    onError: (error: AxiosError<{detail?: string[]; picture?: string[]}>) => handleErrorWithToast(error),
  });

  useEffectOnce(() => {
    setState(draft => {
      draft.country = user.country ?? "Switzerland";
    });
  });

  useEffect(() => {
    if (!isEmpty(state)) {
      setErrors({});
      const result = AddressSchema.safeParse(state);

      if (!result.success) {
        result.error.issues.map(issue => {
          setErrors(state => {
            if (state) {
              state[issue.path[0]] = issue.message;
            } else {
              state = {[issue.path[0]]: issue.message};
            }
          });
        });
      }
    }
  }, [state.address_city, state.address_postal, state.address_street]);

  return (
    <Modal
      onClose={() => {
        if (!isDynamicAddressFocused) {
          setIsModalOpen(false);
          resetRegistrationStore();
        }
      }}
      customBottomRow={<></>}
      backgroundClassName="max-w-3xl"
      modalTitle={t(agency?.large_brand ? "new_company" : "new_customer")}>
      <div className="mb-10 flex flex-col items-center gap-5 self-stretch">
        <div className="mt-10 flex flex-col items-center">
          <div className="mb-5 size-20 shrink-0">
            <img src={RefluencedLogoSrc} className="size-full object-contain" />
          </div>
          <p className="text-neutral-black text-sm font-semibold">
            {t(agency?.large_brand ? "multi_account:company_creation" : "multi_account:customer_creation")}
          </p>
        </div>
        <span className="text-neutral-black text-xl font-semibold">
          {t(
            agency?.large_brand
              ? "multi_account:add_your_companies_logo_here"
              : "multi_account:add_your_customer_logo_here",
          )}
        </span>
        <FileUpload
          file={state?.file}
          onUpload={files =>
            setState(draft => {
              draft.file = files[0];
            })
          }
        />
      </div>
      <div className="flex w-full flex-col gap-1">
        <label htmlFor="firstName" className="text-neutral-grey-dark text-sm font-medium">
          {t("multi_account:customers_name")}
        </label>
        <RegistrationInputField
          storeKey="firstName"
          value={state.company_name}
          onChange={v =>
            setState(draft => {
              draft.company_name = v;
            })
          }
          placeholder={t("multi_account:your_companies_name")}
        />
      </div>
      <AddressForm
        cityKey="address_city"
        countryKey="country"
        errors={errors}
        inputClassName="border-neutral-grey-light"
        onDynamicAddressBlur={() => setDynamicAddressFocused(false)}
        onDynamicAddressFocus={() => setDynamicAddressFocused(true)}
        placeDescriptionKey="place_description"
        placeIdKey="place_id"
        postalKey="address_postal"
        storeForm={state}
        storeSetter={<K extends keyof TCustomerState>(key: K, value: TCustomerState[K]) => {
          setState(draft => {
            draft[key] = value;
          });
        }}
        streetKey="address_street"
        title={agency?.large_brand ? "multi_account:companies_address" : "multi_account:customers_address"}
      />
      <RegistrationInputField
        value={state.website}
        onChange={v =>
          setState(draft => {
            draft.website = v;
          })
        }
        storeKey="companyWebsite"
        label={t(agency?.large_brand ? "multi_account:companies_website" : "multi_account:customers_website")}
        placeholder={"https://refluenced.ch/"}
      />
      <div className="flex w-full flex-col gap-5">
        <InstagramField
          label={t(agency?.large_brand ? "multi_account:companies_instagram" : "multi_account:customers_instagram")}
        />
        <TikTokField
          label={t(agency?.large_brand ? "multi_account:companies_tiktok" : "multi_account:customers_tiktok")}
          optional
        />
        <InterestSelection />
        <div className="flex w-full flex-col gap-2">
          <label
            className="text-neutral-black text-center text-base font-semibold md:text-start md:text-lg"
            htmlFor="bio">
            {t(
              agency?.large_brand
                ? "multi_account:write_a_short_bio_for_your_company"
                : "multi_account:write_a_short_bio",
            )}
          </label>
          <textarea
            id="bio"
            onChange={e =>
              setState(draft => {
                draft.bio = e.target.value;
              })
            }
            className="border-primary-light-solid text-neutral-black placeholder:text-neutral-grey-light focus:border-primary-medium-solid flex h-36 w-full resize-none rounded-md border p-4 pr-0 outline-hidden transition-all duration-150 placeholder:text-sm md:placeholder:text-base"
            placeholder={t("multi_account:company_bio_goes_here")}
          />
        </div>
      </div>
      <Button
        onClick={() => mutate()}
        buttonStyle="Blue"
        isDisabled={!state.company_name || !state.country || !insta || !interests.length}
        isLoading={isPending}
        className="self-center px-16">
        {t("common:create")}
      </Button>
    </Modal>
  );
};

export default CreateCustomerModal;
